import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from 'designSystem/components/Tooltip'
import React from 'react'
import styled, { css } from 'styled-components'

import { SHEET_COLORS, SHEET_FONTS, SHEET_SIZES } from './sheetTheme'

export const SheetTitle = styled.h1`
  margin: 0;
  font-size: ${SHEET_SIZES.title}pt;
  font-weight: 700;
  line-height: 1.15;
`

export const SheetMutedLine = styled.div`
  margin-top: 4pt;
  padding-bottom: 8pt;
  border-bottom: 1px solid ${SHEET_COLORS.rule};
  color: ${SHEET_COLORS.muted};
`

export const SectionHeading = styled.h2`
  margin: 10pt 0 4pt;
  font-size: ${SHEET_SIZES.heading}pt;
  font-weight: 700;
  line-height: 1.2;
`

export const SubHeading = styled.h3`
  margin: 6pt 0 2pt;
  font-size: ${SHEET_SIZES.body}pt;
  font-weight: 700;
  line-height: 1.25;
`

export const Paragraph = styled.p`
  margin: 0 0 6pt;
`

export const Blockquote = styled.blockquote`
  margin: 0 0 6pt;
  padding-left: 8pt;
  border-left: 2px solid ${SHEET_COLORS.rule};
  color: ${SHEET_COLORS.muted};

  > p:last-child {
    margin-bottom: 0;
  }
`

export const List = styled.ul`
  margin: 0 0 6pt;
  padding-left: 14pt;

  li {
    margin: 0 0 2pt;
  }

  li > p {
    margin: 0;
  }
`

export const OrderedList = styled(List).attrs({ as: 'ol' })``

const CHANGED_RULE_PX = 2
const CHANGED_INSET_PT = 8

/** Left rule for content the latest revision changed. */
export const changedOutline = css`
  position: relative;
  margin-left: -${CHANGED_INSET_PT}pt;
  padding-left: calc(${CHANGED_INSET_PT}pt - ${CHANGED_RULE_PX}px);
  border-left: ${CHANGED_RULE_PX}px solid ${SHEET_COLORS.rule};
`

export const ChangedSection = styled.section<{ $changed: boolean }>`
  ${(props) => (props.$changed ? changedOutline : '')}
`

export const UpdatedCaption = styled.span`
  margin-left: 6pt;
  font-size: ${SHEET_SIZES.small}pt;
  font-weight: 400;
  color: ${SHEET_COLORS.muted};
  vertical-align: middle;
`

export const SheetLink = styled.a`
  color: inherit;
  text-decoration: underline;
  text-underline-offset: 0.1em;
`

export const Strong = styled.strong`
  font-weight: 700;
`

export const Emphasis = styled.em`
  font-style: italic;
`

export const Strike = styled.del`
  text-decoration: line-through;
`

/** Identifier-like values: document numbers, equipment tags, part numbers, code spans. */
export const Mono = styled.span`
  font-family: ${SHEET_FONTS.mono};
`

export const CodeBlock = styled.pre`
  margin: 0 0 6pt;
  font-family: ${SHEET_FONTS.mono};
  font-size: ${SHEET_SIZES.body}pt;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
`

const MissingMark = styled.span`
  color: ${SHEET_COLORS.accent};
  font-weight: 700;
  cursor: default;
`

const ReasonList = styled.ul`
  margin: 0;
  padding-left: 1em;
`

/** The red `***` for a missing value; hovering lists the open-item reasons when the section has any. */
export function MissingValue({
  reasons = [],
}: {
  reasons?: readonly string[]
}): React.ReactElement {
  if (reasons.length === 0)
    return <MissingMark data-missing=''>***</MissingMark>
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <MissingMark data-missing='' tabIndex={0}>
          ***
        </MissingMark>
      </TooltipTrigger>
      <TooltipContent side='top' width='measure'>
        {reasons.length === 1 ? (
          reasons[0]
        ) : (
          <ReasonList>
            {reasons.map((reason) => (
              <li key={reason}>{reason}</li>
            ))}
          </ReasonList>
        )}
      </TooltipContent>
    </Tooltip>
  )
}

export type CellAlign = 'left' | 'right' | 'center'

const cellAlign = css<{ $align?: CellAlign }>`
  text-align: ${(props) => props.$align ?? 'left'};
  font-variant-numeric: ${(props) =>
    props.$align === 'right' ? 'tabular-nums' : 'normal'};
`

export const Table = styled.table`
  width: 100%;
  margin: 2pt 0 8pt;
  border-collapse: collapse;
  table-layout: auto;
`

/** Small uppercase muted label with a rule below: the one convention for table headers and section labels. */
export const labelText = css`
  font-size: ${SHEET_SIZES.small}pt;
  font-weight: 400;
  text-transform: uppercase;
  color: ${SHEET_COLORS.muted};
`

export const HeadCell = styled.th<{ $align?: CellAlign }>`
  ${labelText}
  padding: 0 6pt 2pt 0;
  border-bottom: 1px solid ${SHEET_COLORS.rule};
  vertical-align: bottom;
  ${cellAlign}

  &:last-child {
    padding-right: 0;
  }
`

export const BodyCell = styled.td<{ $align?: CellAlign }>`
  padding: 3pt 6pt 3pt 0;
  border-bottom: 0.5px solid ${SHEET_COLORS.rule};
  vertical-align: top;
  overflow-wrap: anywhere;
  ${cellAlign}

  &:last-child {
    padding-right: 0;
  }
`
